'use client';

import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import Progress from '@precis-digital/kurama/Progress';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import alvieLogoFull from 'public/assets/images/alvie_logo_full.svg';
import piechart3D from 'public/assets/images/piechart_3d.png';
import { type ReactElement, type ReactNode } from 'react';
import ThemedImage from 'shared/components/Themed/Image';
import { useTranslation } from 'shared/translations';
interface ProgressBarLoaderProps {
  header?: ReactNode | null;
  headerImage?: ReactNode | null;
  title?: string | null;
  description?: string | null;
  logo?: ReactNode;
  completionElement?: ReactNode;
}
const ProgressBarLoader = t0 => {
  const $ = _c(33);
  const {
    header,
    headerImage,
    title,
    description,
    completionElement
  } = t0;
  const {
    t
  } = useTranslation("common");
  let t1;
  if ($[0] !== t) {
    t1 = t("progressBarLoader.alvieIconAltTag");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: "100%",
      height: "auto"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <StyledGridLogoContainer><ThemedImage priority={true} src={alvieLogoFull} alt={t1} width={128} height={64} style={t2} /></StyledGridLogoContainer>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== headerImage) {
    t4 = headerImage ?? <ThemedImage src={piechart3D} priority={true} alt="piechart" width={540} height={232} style={{
      maxWidth: "100%",
      height: "auto"
    }} />;
    $[5] = headerImage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== header || $[8] !== t) {
    t5 = header ?? t("progressBarLoader.header");
    $[7] = header;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <Typography variant="h2">{t5}</Typography>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== completionElement) {
    t7 = completionElement ?? <StyledProgress value={0} variant="indeterminate" />;
    $[12] = completionElement;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t || $[15] !== title) {
    t8 = title ?? t("progressBarLoader.subHeader");
    $[14] = t;
    $[15] = title;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <Typography variant="h5">{t8}</Typography>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== description || $[20] !== t) {
    t10 = description ?? t("progressBarLoader.description");
    $[19] = description;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <Typography variant="body2">{t10}</Typography>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t4 || $[26] !== t6 || $[27] !== t7 || $[28] !== t9) {
    t12 = <StyledGridContentContainer>{t4}{t6}{t7}{t9}{t11}</StyledGridContentContainer>;
    $[24] = t11;
    $[25] = t4;
    $[26] = t6;
    $[27] = t7;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t3) {
    t13 = <StyledGridPageContainer>{t3}{t12}</StyledGridPageContainer>;
    $[30] = t12;
    $[31] = t3;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};
const StyledProgress = styled(Progress)(({
  theme
}) => ({
  '> span:first-of-type': {
    height: '16px',
    display: 'inline-flex',
    width: '294px',
    marginTop: '24px',
    marginBottom: '16px',
    'span:first-of-type': {
      backgroundColor: theme.tokens.background.brand.main.default
    }
  }
}));
const StyledGridPageContainer = styled(Grid)(({
  theme
}) => ({
  padding: '48px 48px 96px 48px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
  minHeight: '100vh',
  maxWidth: '1280px',
  margin: 'auto',
  position: 'relative',
  backgroundColor: theme.tokens.background.main.default,
  color: theme.tokens.foreground.main.primary
}));
const StyledGridLogoContainer = styled(Grid)(({
  theme
}) => ({
  position: 'absolute',
  height: '64px',
  width: '100%',
  display: 'flex',
  gap: '26px',
  padding: '14px'
}));
const StyledGridContentContainer = styled(Grid)(() => ({
  alignContent: 'center',
  alignSelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  margin: 'auto',
  textAlign: 'center',
  padding: '32px 0px',
  '> h2': {
    whiteSpace: 'nowrap',
    marginTop: '56px'
  }
}));
export default ProgressBarLoader;